import { html, state } from 'lit-element'
import type { SwitchComponent } from '@getgo/chameleon-web'
import dndSwitchStyles from './dnd-switch.styles.scss'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import { getShellLogger } from '../../common/logger'
import { getIdFromUserProfile } from '../../services/identity/userProfile'
import { displayDNDSnackbar } from './dnd-snackbar'

import type { Listener, EventBus } from '../../services/namespaces'
import { PresenceServiceNamespace, UserDoNotDisturb } from '../../services/presence'
import type { PresenceEvents, ShellPresenceService, UserPresenceUpdated } from '../../services/presence'
import { ShellElement } from '../../common/shell-element'
import type { ExternalUserKey } from '@ucc/common'

export class GoToDndSwitch extends ShellElement {
  static readonly tagName = 'goto-dnd-switch'

  @state() private internalIsDoNotDisturb = false
  @state() private isSwitchEnabled = false

  static get styles() {
    return dndSwitchStyles
  }

  private presenceService?: ShellPresenceService
  private eventBus: EventBus | undefined
  private externalUserKey?: Readonly<ExternalUserKey>

  connectedCallback() {
    super.connectedCallback()
    const { eventBus, presence, identity } = getShellApiInstance()

    this.eventBus = eventBus
    this.presenceService = presence
    const externalUserKey = getIdFromUserProfile(identity?.me)
    this.externalUserKey = externalUserKey
    Object.freeze(this.externalUserKey)

    if (this.presenceService) {
      this.initUserPresence()
    }

    if (this.externalUserKey && this.eventBus) {
      this.listenForPresenceUpdatedEvent()
    }
  }

  private listenForPresenceUpdatedEvent() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { userPresenceUpdate } = this.eventBus!.subscribeTo<typeof PresenceServiceNamespace, typeof PresenceEvents>(
      PresenceServiceNamespace,
    )

    const externalUserKey = this.externalUserKey

    // When we receive a specific userDoNotDisturb change
    const userPresenceUpdatedHandler: Listener<UserPresenceUpdated> = async (payload): Promise<boolean> => {
      const isApplicable = payload.externalUserKey === externalUserKey
      const userDoNotDisturb = payload.userDoNotDisturb
      if (isApplicable) {
        this.internalIsDoNotDisturb = userDoNotDisturb === UserDoNotDisturb.DO_NOT_DISTURB
      }

      await this.updateComplete

      return isApplicable
    }

    userPresenceUpdate.on(userPresenceUpdatedHandler)

    this.unsubscribeFunctions.push(() => {
      userPresenceUpdate.removeListener(userPresenceUpdatedHandler)
    })
  }

  private async initUserPresence() {
    this.isSwitchEnabled = true
    try {
      const userPresence = await this.presenceService?.getUserPresence()
      const internalIsDoNotDisturb = userPresence?.userDoNotDisturb === UserDoNotDisturb.DO_NOT_DISTURB
      this.internalIsDoNotDisturb = internalIsDoNotDisturb
    } catch (error) {
      // https://jira.ops.expertcity.com/browse/SCORE-1193
      this.isSwitchEnabled = false
      // TODO make shell logger to be usable elsewhere than here
      // TODO replace this log by an alert to the user [SCORE-1193]
      getShellLogger().error(`Could not get user presence: ${error}`)
    }
  }

  async activate() {
    if (this.isSwitchEnabled === false) {
      // Situation: Trying to change state if is not enabled by clicking should not pass
      return
    }
    this.isSwitchEnabled = false

    const currentState = this.isDoNotDisturb
    // Flipping the state
    const nextState = currentState ? UserDoNotDisturb.NONE : UserDoNotDisturb.DO_NOT_DISTURB

    try {
      const userPresence = await this.presenceService?.setUserPresence({ userDoNotDisturb: nextState })
      // Is there discrepancy between received userPresence and flipped state above?
      if (nextState !== userPresence?.userDoNotDisturb) {
        // TODO make shell logger to be usable elsewhere than here (like here!) — Tests would break
        getShellLogger().error(
          `We received a different response from presenceService when setting userDoNotDisturb to "${nextState}". ` +
            `It returned with something different: "${userPresence?.userDoNotDisturb}"`,
        )
      }

      this.internalIsDoNotDisturb = nextState === UserDoNotDisturb.DO_NOT_DISTURB
      displayDNDSnackbar(nextState)
    } finally {
      // Even if this failed, maybe next time it won't.
      this.isSwitchEnabled = true
    }

    await this.updateComplete
  }

  get doNotDisturbStatus(): UserDoNotDisturb {
    return this.internalIsDoNotDisturb ? UserDoNotDisturb.DO_NOT_DISTURB : UserDoNotDisturb.NONE
  }

  get isDoNotDisturb(): SwitchComponent['checked'] {
    return this.internalIsDoNotDisturb
  }

  render() {
    return html`
      <chameleon-switch
        ?disabled=${!this.isSwitchEnabled}
        ?readonly=${!this.isSwitchEnabled}
        ?checked=${this.isDoNotDisturb}
        @click=${this.activate}
        ><slot></slot
      ></chameleon-switch>
    `
  }
}
declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToDndSwitch.tagName]: GoToDndSwitch
  }
}
